import React, { useState, useEffect } from 'react';
import {
    Box,
    Card,
    CardContent,
    Typography,
    Grid,
    TextField,
    Button,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    IconButton,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Paper,
    Chip,
    Divider,
    Alert,
    InputAdornment,
    Collapse,
    useTheme,
    alpha
} from '@mui/material';
import {
    Add as AddIcon,
    Edit as EditIcon,
    Delete as DeleteIcon,
    Save as SaveIcon,
    Cancel as CancelIcon,
    Sanitizer as SanitizerIcon,
    FiberManualRecord as FiberManualRecordIcon,
    CameraRoll as CameraRollIcon,
    AllInbox as AllInboxIcon,
    Schedule as ScheduleIcon,
    ExpandMore as ExpandMoreIcon,
    ExpandLess as ExpandLessIcon,
    Close as CloseIcon,
    Search as SearchIcon
} from '@mui/icons-material';
import { DateTimePicker } from '@mui/x-date-pickers/DateTimePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import dayjs from 'dayjs';
import { supabase } from '../../../../supabaseClient';
import { usePermissions } from '../../../../utils/permissions';
import { useNavigate } from 'react-router-dom';

// Component to display multiple lots for a supply from line item
const SupplyLotsCards = ({ supply, lineItemId, theme, alpha }) => {
    const [supplyLots, setSupplyLots] = useState([]);
    
    useEffect(() => {
        const loadLots = async () => {
            try {
                if (lineItemId) {
                    // Load lots from production_line_item_lots table
                    const { data, error } = await supabase
                        .from('production_line_item_lots')
                        .select('*')
                        .eq('line_item_id', lineItemId);
                    
                    if (!error && data && data.length > 0) {
                        setSupplyLots(data);
                    }
                } else if (supply.id) {
                    // Fallback: load from supply log lots table
                    const { data, error } = await supabase
                        .from('production_line_log_supply_lots')
                        .select('*')
                        .eq('line_supply_log_id', supply.id);
                    
                    if (!error && data && data.length > 0) {
                        setSupplyLots(data);
                    }
                }
            } catch (err) {
                console.error('Error loading lots:', err);
            }
        };
        
        if (lineItemId || supply.id) {
            loadLots();
        }
    }, [lineItemId, supply.id]);
    
    return (
        <>
            {supplyLots.map((lot, lotIndex) => (
                <Grid item xs={12} sm={6} md={4} key={lotIndex}>
                    <Card sx={{
                        p: 2,
                        border: `1px solid ${theme.palette.grey[400]}`,
                        borderRadius: 3,
                        backgroundColor: alpha(theme.palette.info.main, 0.05),
                        boxShadow: `0 4px 20px ${alpha(theme.palette.primary.main, 0.08)}`,
                        transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                        '&:hover': {
                            transform: 'translateY(-2px)',
                            boxShadow: `0 8px 32px ${alpha(theme.palette.primary.main, 0.12)}`
                        }
                    }}>
                        <Typography variant="subtitle2" sx={{ 
                            fontWeight: 500, 
                            color: theme.palette.info.main,
                            mb: 1
                        }}>
                            {lot.lot_number ? `Lot ${lot.lot_number}` : `Batch ${lot.batch_number}`}
                        </Typography>
                        <Typography variant="body2" sx={{ 
                            color: theme.palette.text.secondary,
                            mb: 0.5
                        }}>
                            Quantity: {lot.quantity_used || lot.unit_used} {lot.unit_used}
                        </Typography>
                        {lot.location_name && (
                            <Typography variant="caption" sx={{ 
                                color: theme.palette.text.secondary,
                                display: 'block'
                            }}>
                                Location: {lot.location_name}
                            </Typography>
                        )}
                        {lot.expiry_date && (
                            <Typography variant="caption" sx={{ 
                                color: theme.palette.text.secondary,
                                display: 'block'
                            }}>
                                Expires: {new Date(lot.expiry_date).toLocaleDateString()}
                            </Typography>
                        )}
                        {lot.manufactured_date && (
                            <Typography variant="caption" sx={{ 
                                color: theme.palette.text.secondary,
                                display: 'block'
                            }}>
                                Manufactured: {new Date(lot.manufactured_date).toLocaleDateString()}
                            </Typography>
                        )}
                    </Card>
                </Grid>
            ))}
        </>
    );
};

export default function SupplyLog({ 
    supplies = [], 
    setSupplies, 
    lineLogId, 
    lineProductionData, 
    lineItems = [],
    onSuppliesUpdate,
    onSupplyLogComplete,
    showSnackbar,
    lineTimeFormat = '12hour'
}) {
    const theme = useTheme();
    const navigate = useNavigate();
    const { hasPermission } = usePermissions();
    const [openDialog, setOpenDialog] = useState(false);
    const [editingSupply, setEditingSupply] = useState(null);
    const [expandedSupplies, setExpandedSupplies] = useState(new Set());
    const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
    const [supplyToDelete, setSupplyToDelete] = useState(null);
    
    // Line item selection dialog state
    const [lineItemSelectionDialogOpen, setLineItemSelectionDialogOpen] = useState(false);
    const [lineItemSearchTerm, setLineItemSearchTerm] = useState('');
    
    // Multiple lots state for current supply
    const [supplyLots, setSupplyLots] = useState([]);
    
    const [formData, setFormData] = useState({
        line_item_id: '',
        supply_name: '',
        item_type: '',
        item_id: null,
        supplier_name: '',
        planned_quantity: '',
        actual_quantity: '',
        time_of_usage: dayjs(),
        used_by_operator: '',
        usage_order: supplies.length + 1,
        additional_notes: ''
    });

    const sortItemsByTemplateOrder = (items) => {
        // Define the preferred order: cap → bottle → label → supply
        const typeOrder = {
            'cap': 0,
            'caps': 0,
            'bottle': 1,
            'bottles': 1,
            'label': 2,
            'labels': 2,
            'supply': 3,
            'supplies': 3
        };

        return items.sort((a, b) => {
            const orderA = typeOrder[a.item_type] ?? 999;
            const orderB = typeOrder[b.item_type] ?? 999;
            
            // If same type order, sort by name
            if (orderA === orderB) {
                const nameA = getItemName(a) || '';
                const nameB = getItemName(b) || '';
                return nameA.localeCompare(nameB);
            }
            
            return orderA - orderB;
        });
    };

    // Get available line items that haven't been logged yet
    const getAvailableLineItems = () => {
        const loggedItemIds = supplies.map(sup => sup.line_item_id).filter(Boolean);
        const availableItems = lineItems.filter(item => !loggedItemIds.includes(item.id));
        
        return sortItemsByTemplateOrder(availableItems);
    };

    // Line item selection dialog functions
    const handleOpenLineItemSelection = () => {
        setLineItemSearchTerm('');
        setLineItemSelectionDialogOpen(true);
    };

    const handleCloseLineItemSelection = () => {
        setLineItemSelectionDialogOpen(false);
        setLineItemSearchTerm('');
    };

    const handleSelectLineItem = (lineItem) => {
        handleLineItemSelect(lineItem.id);
        handleCloseLineItemSelection();
    };

    const getFilteredLineItems = () => {
        const available = getAvailableLineItems();
        if (!lineItemSearchTerm.trim()) return available;
        
        const searchTerm = lineItemSearchTerm.toLowerCase();
        return available.filter(item => 
            getItemName(item)?.toLowerCase().includes(searchTerm) ||
            getSupplierName(item)?.toLowerCase().includes(searchTerm) ||
            item.lot_numbers?.some(lot => lot.toLowerCase().includes(searchTerm)) ||
            item.notes?.toLowerCase().includes(searchTerm)
        );
    };

    const getItemIcon = (itemType) => {
        switch (itemType) {
            case 'bottle': return <SanitizerIcon />;
            case 'bottles': return <SanitizerIcon />;
            case 'cap': return <FiberManualRecordIcon />;
            case 'caps': return <FiberManualRecordIcon />;
            case 'label': return <CameraRollIcon />;
            case 'labels': return <CameraRollIcon />;
            case 'supply': return <AllInboxIcon />;
            case 'supplies': return <AllInboxIcon />;
            default: return <AllInboxIcon />;
        }
    };

    const getItemName = (item) => {
        switch (item.item_type) {
            case 'bottle': return item.bottle?.name;
            case 'bottles': return item.bottles?.name;
            case 'cap': return item.cap?.name;
            case 'caps': return item.caps?.name;
            case 'label': return item.label?.name;
            case 'labels': return item.labels?.name;
            case 'supply': return item.supply?.name;
            case 'supplies': return item.supplies?.name;
            default: return 'Unknown Item';
        }
    };

    const getSupplierName = (item) => {
        switch (item.item_type) {
            case 'bottle': return item.bottle?.supplier?.name;
            case 'bottles': return item.bottles?.supplier?.name;
            case 'cap': return item.cap?.supplier?.name;
            case 'caps': return item.caps?.supplier?.name;
            case 'label': return item.label?.supplier?.name;
            case 'labels': return item.labels?.supplier?.name;
            case 'supply': return item.supply?.supplier?.name;
            case 'supplies': return item.supplies?.supplier?.name;
            default: return '';
        }
    };

    const handleAddSupply = () => {
        setEditingSupply(null);
        setSupplyLots([]); // Clear lots for new supply
        const availableItems = getAvailableLineItems();
        const nextOrder = Math.max(...supplies.map(s => s.usage_order || 0), 0) + 1;
        
        setFormData({
            line_item_id: '',
            supply_name: '',
            item_type: '',
            item_id: null,
            supplier_name: '',
            planned_quantity: '',
            actual_quantity: '',
            time_of_usage: dayjs(),
            used_by_operator: lineProductionData?.operator_name || '',
            usage_order: nextOrder,
            additional_notes: ''
        });
        setOpenDialog(true);
    };

    const handleEditSupply = async (supply) => {
        setEditingSupply(supply);
        
        // Load existing lots for this supply
        try {
            const { data: lotsData, error: lotsError } = await supabase
                .from('production_line_log_supply_lots')
                .select('*')
                .eq('line_supply_log_id', supply.id)
                .order('created_at');

            if (lotsError) {
                console.error('Error loading existing supply lots:', lotsError);
                setSupplyLots([]);
            } else {
                setSupplyLots(lotsData || []);
            }
        } catch (error) {
            console.error('Error loading existing supply lots:', error);
            setSupplyLots([]);
        }

        setFormData({
            line_item_id: supply.line_item_id || '',
            supply_name: supply.supply_name || '',
            item_type: supply.item_type || '',
            item_id: supply.item_id || null,
            supplier_name: supply.supplier_name || '',
            planned_quantity: supply.planned_quantity || '',
            actual_quantity: supply.actual_quantity || '',
            time_of_usage: supply.time_of_usage ? dayjs(supply.time_of_usage) : dayjs(),
            used_by_operator: supply.used_by_operator || lineProductionData?.operator_name || '',
            usage_order: supply.usage_order || 1,
            additional_notes: supply.additional_notes || ''
        });
        setOpenDialog(true);
    };

    const handleLineItemSelect = async (lineItemId) => {
        const lineItem = lineItems.find(item => item.id === lineItemId);
        if (!lineItem) return;

        // Load lot selections from production_line_item_lots table
        try {
            const { data: lotsData, error: lotsError } = await supabase
                .from('production_line_item_lots')
                .select('*')
                .eq('line_item_id', lineItemId)
                .order('created_at');

            if (lotsError) {
                console.error('Error loading item lots:', lotsError);
                setSupplyLots([]);
            } else {
                setSupplyLots(lotsData || []);
            }
        } catch (error) {
            console.error('Error loading item lots:', error);
            setSupplyLots([]);
        }

        // Auto-populate ALL data from line item
        const itemTypeKey = lineItem.item_type === 'bottle' ? 'bottle' :
                          lineItem.item_type === 'cap' ? 'cap' :
                          lineItem.item_type === 'label' ? 'label' :
                          lineItem.item_type === 'supply' ? 'supply' : 
                          lineItem.item_type;

        setFormData(prev => ({
            ...prev,
            line_item_id: lineItemId,
            supply_name: getItemName(lineItem) || '',
            item_type: lineItem.item_type || '',
            item_id: lineItem.item_id || null,
            supplier_name: getSupplierName(lineItem) || '',
            planned_quantity: lineItem.planned_quantity?.toString() || '',
            actual_quantity: lineItem.actual_quantity?.toString() || lineItem.planned_quantity?.toString() || '',
            used_by_operator: lineProductionData?.operator_name || '',
            // Also populate any notes from line item
            additional_notes: lineItem.notes || ''
        }));
    };

    const createProductionLineLog = async () => {
        if (lineLogId) return lineLogId;

        try {
            const { data, error } = await supabase
                .from('production_line_logs')
                .insert({
                    line_production_id: lineProductionData?.id,
                    company_id: lineProductionData?.company_id,
                    operator_name: lineProductionData?.operator_name,
                    shift_start_time: dayjs().toISOString(),
                    status: 'in_progress'
                })
                .select()
                .single();

            if (error) throw error;
            return data.id;
        } catch (error) {
            console.error('Error creating production line log:', error);
            return null;
        }
    };

    const handleSaveSupply = async () => {
        // Check permissions before proceeding
        if (!canEdit) {
            showSnackbar?.('You do not have permission to save supply log.', 'error');
            return;
        }

        try {
            // Validation for required fields
            const requiredFields = [];
            
            if (!formData.line_item_id) requiredFields.push('Line Item Selection');
            if (!formData.actual_quantity) requiredFields.push('Actual Quantity');
            if (!formData.time_of_usage) requiredFields.push('Time of Usage');
            if (!formData.used_by_operator) requiredFields.push('Used by Operator');
            
            if (requiredFields.length > 0) {
                showSnackbar?.(`Please fill in all required fields: ${requiredFields.join(', ')}`, 'error');
                return;
            }

            const logId = await createProductionLineLog();
            if (!logId) {
                console.error('Failed to create or get line log ID for supply');
                showSnackbar?.('Failed to create line log', 'error');
                return;
            }

            const saveData = {
                line_log_id: logId,
                company_id: lineProductionData?.company_id,
                line_item_id: formData.line_item_id || null,
                supply_name: formData.supply_name,
                item_type: formData.item_type || null,
                item_id: formData.item_id || null,
                supplier_name: formData.supplier_name || null,
                time_of_usage: formData.time_of_usage.toISOString(),
                used_by_operator: formData.used_by_operator || null,
                usage_order: formData.usage_order ? parseInt(formData.usage_order) : null,
                additional_notes: formData.additional_notes || null,
                // Convert empty strings to null for numeric fields
                planned_quantity: formData.planned_quantity ? parseFloat(formData.planned_quantity) : null,
                actual_quantity: formData.actual_quantity ? parseFloat(formData.actual_quantity) : null
            };

            let result;
            if (editingSupply) {
                const { data, error } = await supabase
                    .from('production_line_log_supplies')
                    .update(saveData)
                    .eq('id', editingSupply.id)
                    .select()
                    .single();
                if (error) {
                    console.error('Error updating supply:', error);
                    showSnackbar?.('Error updating supply: ' + error.message, 'error');
                    return;
                }
                result = data;
                
                setSupplies(prev => 
                    prev.map(sup => sup.id === editingSupply.id ? result : sup)
                );
                showSnackbar?.('Supply updated successfully', 'success');
            } else {
                const { data, error } = await supabase
                    .from('production_line_log_supplies')
                    .insert(saveData)
                    .select()
                    .single();
                if (error) {
                    console.error('Error inserting supply:', error);
                    showSnackbar?.('Error adding supply: ' + error.message, 'error');
                    return;
                }
                result = data;
                
                setSupplies(prev => [...prev, result]);
                showSnackbar?.('Supply added successfully', 'success');
            }

            // Save multiple lots to production_line_log_supply_lots table
            if (supplyLots.length > 0) {
                try {
                    // First, delete existing lot records if editing
                    if (editingSupply) {
                        await supabase
                            .from('production_line_log_supply_lots')
                            .delete()
                            .eq('line_supply_log_id', result.id);
                    }

                    // Insert new lot records based on the supplyLots data
                    const lotRecords = supplyLots.map(lot => ({
                        line_supply_log_id: result.id,
                        inventory_item_id: lot.inventory_item_id || lot.id,
                        quantity_used: lot.quantity_used || lot.quantity_on_hand,
                        unit_used: lot.unit_used || lot.unit_of_measure,
                        lot_number: lot.lot_number,
                        batch_number: lot.batch_number,
                        expiry_date: lot.expiry_date,
                        manufactured_date: lot.manufactured_date,
                        location_name: lot.location_name || lot.location?.name,
                        notes: lot.notes || null
                    }));

                    const { error: lotsError } = await supabase
                        .from('production_line_log_supply_lots')
                        .insert(lotRecords);

                    if (lotsError) {
                        console.error('Error saving supply lots:', lotsError);
                        showSnackbar?.('Supply saved but error saving lot details: ' + lotsError.message, 'warning');
                    }
                } catch (lotError) {
                    console.error('Error saving supply lots:', lotError);
                    showSnackbar?.('Supply saved but error saving lot details', 'warning');
                }
            }

            setOpenDialog(false);
            setSupplyLots([]); // Clear lots for next supply
            
            // Call refresh callback to update parent component
            if (onSuppliesUpdate) {
                onSuppliesUpdate();
            }
        } catch (error) {
            console.error('Error saving supply:', error);
            showSnackbar?.('Error saving supply: ' + error.message, 'error');
        }
    };

    const handleDeleteSupply = async (supplyId) => {
        // Check permissions before proceeding
        if (!canDelete) {
            showSnackbar?.('You do not have permission to delete supply log entries.', 'error');
            return;
        }

        try {
            const { error } = await supabase
                .from('production_line_log_supplies')
                .delete()
                .eq('id', supplyId);

            if (error) throw error;

            setSupplies(prev => prev.filter(sup => sup.id !== supplyId));
            showSnackbar?.('Supply deleted successfully', 'success');
            
            // Call refresh callback to update parent component
            if (onSuppliesUpdate) {
                onSuppliesUpdate();
            }
        } catch (error) {
            console.error('Error deleting supply:', error);
            showSnackbar?.('Error deleting supply: ' + error.message, 'error');
        }
    };

    const confirmDelete = (supply) => {
        setSupplyToDelete(supply);
        setDeleteConfirmOpen(true);
    };

    const handleConfirmDelete = () => {
        if (supplyToDelete) {
            handleDeleteSupply(supplyToDelete.id);
        }
        setDeleteConfirmOpen(false);
        setSupplyToDelete(null);
    };

    const toggleSupplyExpansion = (supplyId) => {
        setExpandedSupplies(prev => {
            const newSet = new Set(prev);
            if (newSet.has(supplyId)) {
                newSet.delete(supplyId);
            } else {
                newSet.add(supplyId);
            }
            return newSet;
        });
    };

    const availableLineItems = getAvailableLineItems();
    const allItemsLogged = lineItems.length > 0 && availableLineItems.length === 0;

    // Track completion status to avoid unnecessary calls
    const [lastCompletionStatus, setLastCompletionStatus] = React.useState(null);

    // Check if supply log should be marked as complete - only when status actually changes
    React.useEffect(() => {
        if (!onSupplyLogComplete) return;

        const shouldBeComplete = lineItems.length > 0 && supplies.length > 0 && allItemsLogged;
        
        // Only call if status actually changed
        if (shouldBeComplete !== lastCompletionStatus) {
            setLastCompletionStatus(shouldBeComplete);
            onSupplyLogComplete(shouldBeComplete);
        }
    }, [lineItems.length, supplies.length, allItemsLogged, onSupplyLogComplete, lastCompletionStatus]);

    // Permission check - must be after all hooks
    if (!hasPermission('production.line.log.supply.view', 'read')) {
        return (
            <Box sx={{ 
                display: 'flex', 
                flexDirection: 'column', 
                alignItems: 'center', 
                justifyContent: 'center',
                minHeight: '60vh',
                textAlign: 'center',
                width: '100%',
                px: 3,
                py: 8
            }}>
                <Card sx={{
                    p: 6,
                    borderRadius: 3,
                    border: `1px solid ${theme.palette.grey[400]}`,
                    background: 'white',
                    boxShadow: `0 12px 40px ${alpha(theme.palette.grey[900], 0.08)}`,
                    maxWidth: 500,
                    width: '100%',
                    position: 'relative',
                    overflow: 'hidden',
                    cursor: 'default',
                    transition: 'all 0.4s cubic-bezier(0.175, 0.885, 0.32, 1.275)',
                    '&:hover': {
                        transform: 'translateY(-8px)',
                        boxShadow: `0 20px 60px ${alpha(theme.palette.grey[900], 0.15)}`,
                        border: `1px solid ${alpha(theme.palette.primary.main, 0.3)}`
                    },
                    '&::before': {
                        content: '""',
                        position: 'absolute',
                        top: 0,
                        left: 0,
                        right: 0,
                        height: 4,
                        background: `linear-gradient(90deg, ${theme.palette.primary.main}, ${theme.palette.grey[400]})`,
                        opacity: 0.8,
                        transition: 'opacity 0.3s ease'
                    },
                    '&:hover::before': {
                        opacity: 1
                    }
                }}>
                    <Box sx={{ 
                        p: 2, 
                        borderRadius: 3, 
                        background: `linear-gradient(${alpha(theme.palette.primary.main, 0.05)}, ${alpha(theme.palette.primary.main, 0.02)})`,
                        border: `1px solid ${alpha(theme.palette.primary.main, 0.15)}`,
                        mb: 4,
                        display: 'inline-flex',
                        boxShadow: `0 8px 24px ${alpha(theme.palette.primary.main, 0.15)}`
                    }}>
                        <AllInboxIcon sx={{ fontSize: 40, color: 'text.secondary' }} />
                    </Box>
                    <Typography variant="h4" sx={{ 
                        mb: 2, 
                        fontWeight: 500, 
                        color: 'text.primary',
                        fontSize: '2rem',
                        letterSpacing: '-0.5px'
                    }}>
                        Access Restricted
                    </Typography>
                    <Typography variant="h6" sx={{ 
                        color: 'text.secondary', 
                        mb: 3,
                        fontWeight: 500,
                        fontSize: '1.1rem'
                    }}>
                        Supply Log Permissions Required
                    </Typography>
                    <Typography variant="body1" sx={{ 
                        color: 'text.secondary',
                        lineHeight: 1.6,
                        mb: 4,
                        fontSize: '1rem',
                        maxWidth: 400,
                        mx: 'auto'
                    }}>
                        You don't have permission to view the Supply Log. 
                        Contact your administrator to request access.
                    </Typography>
                </Card>
            </Box>
        );
    }

    // Check permissions  
    const canEdit = hasPermission('production.line.log.supply.edit', 'write');
    const canDelete = hasPermission('production.line.log.supply.delete', 'delete');

    return (
        <LocalizationProvider dateAdapter={AdapterDayjs}>
            <Box>
                <Grid container spacing={4}>
                    {/* Header */}
                    <Grid item xs={12}>
                        <Card sx={{ 
                            borderRadius: 4,
                            border: `1px solid ${theme.palette.grey[400]}`,
                            background: 'white',
                            backdropFilter: 'blur(20px)',
                            boxShadow: `0 8px 32px ${alpha(theme.palette.primary.main, 0.12)}`,
                            '&:hover': {
                                transform: 'translateY(-4px)',
                                boxShadow: `0 12px 48px ${alpha(theme.palette.primary.main, 0.18)}`,
                            },
                            transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)'
                        }}>
                            <CardContent sx={{ p: 4 }}>
                                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                        <Box sx={{ 
                                            p: 1.5, 
                                            borderRadius: 2, 
                                            background: `linear-gradient(${alpha(theme.palette.primary.main, 0.1)}, ${alpha(theme.palette.primary.main, 0.05)})`,
                                            border: `1px solid ${alpha(theme.palette.primary.main, 0.2)}`,
                                            backdropFilter: 'blur(10px)',
                                            mr: 2
                                        }}>
                                            <AllInboxIcon sx={{ fontSize: 24, color: theme.palette.primary.main }} />
                                        </Box>
                                        <Box>
                                            <Typography variant="h6" sx={{ fontWeight: 500, color: theme.palette.text.primary, mb: 0.5 }}>
                                                Supply Usage Log
                                            </Typography>
                                            <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 500 }}>
                                                Track supply usage, quantities, and lot/batch information for production
                                                {availableLineItems.length > 0 && (
                                                    <> • {availableLineItems.length} items available to log</>
                                                )}
                                            </Typography>
                                        </Box>
                                    </Box>
                                    <Button
                                        variant="contained"
                                        startIcon={<AddIcon />}
                                        onClick={canEdit ? handleAddSupply : null}
                                        disabled={allItemsLogged || !canEdit}
                                        size="large"
                                        sx={{
                                            borderRadius: 3,
                                            px: 4,
                                            py: 1.5,
                                            background: allItemsLogged 
                                                ? theme.palette.grey[400]
                                                : `linear-gradient(135deg, ${theme.palette.primary.main}, ${theme.palette.primary.dark})`,
                                            color: allItemsLogged ? theme.palette.grey[100] : 'white',
                                            fontWeight: 500,
                                            boxShadow: allItemsLogged 
                                                ? 'none' 
                                                : `0 4px 12px ${alpha(theme.palette.primary.main, 0.3)}`,
                                            '&:hover': allItemsLogged ? {} : {
                                                background: `linear-gradient(135deg, ${theme.palette.primary.dark}, ${theme.palette.primary.main})`,
                                                transform: 'translateY(-1px)',
                                                boxShadow: `0 6px 20px ${alpha(theme.palette.primary.main, 0.4)}`
                                            },
                                            '&:disabled': {
                                                background: theme.palette.grey[400],
                                                color: theme.palette.grey[100],
                                                cursor: 'not-allowed'
                                            },
                                            transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)'
                                        }}
                                    >
                                        {allItemsLogged ? 'All Items Logged' : 'Add Supply'}
                                    </Button>
                                </Box>
                                {availableLineItems.length === 0 && lineItems.length > 0 && (
                                    <Alert 
                                        severity="info" 
                                        sx={{ 
                                            mt: 3,
                                            borderRadius: 2,
                                            backgroundColor: alpha(theme.palette.info.main, 0.1),
                                            border: `1px solid ${alpha(theme.palette.info.main, 0.3)}`,
                                            '& .MuiAlert-icon': {
                                                color: theme.palette.info.main
                                            }
                                        }}
                                    >
                                        All line items have been logged. Edit existing entries or add more items in the Production Line Items tab.
                                    </Alert>
                                )}
                                {lineItems.length === 0 && (
                                    <Alert 
                                        severity="warning" 
                                        sx={{ 
                                            mt: 3,
                                            borderRadius: 2,
                                            backgroundColor: alpha(theme.palette.warning.main, 0.1),
                                            border: `1px solid ${alpha(theme.palette.warning.main, 0.3)}`,
                                            '& .MuiAlert-icon': {
                                                color: theme.palette.warning.main
                                            }
                                        }}
                                    >
                                        No line items available. Generate line items first in the Production Line Items tab, or add supplies manually.
                                    </Alert>
                                )}
                            </CardContent>
                        </Card>
                    </Grid>

                    {/* Supplies Display */}
                    <Grid item xs={12}>
                        {supplies.length === 0 ? (
                            <Card sx={{ 
                                borderRadius: 4,
                                border: `1px solid ${theme.palette.grey[400]}`,
                                background: 'white',
                                backdropFilter: 'blur(20px)',
                                boxShadow: `0 8px 32px ${alpha(theme.palette.primary.main, 0.12)}`,
                                '&:hover': {
                                    transform: 'translateY(-4px)',
                                    boxShadow: `0 12px 48px ${alpha(theme.palette.primary.main, 0.18)}`,
                                },
                                transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)'
                            }}>
                                <CardContent sx={{ textAlign: 'center', py: 8 }}>
                                    <AllInboxIcon sx={{ fontSize: 80, color: theme.palette.text.disabled, mb: 3 }} />
                                    <Typography variant="h6" color="text.secondary" gutterBottom sx={{ fontWeight: 500 }}>
                                        No supplies logged yet
                                    </Typography>
                                    <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 500 }}>
                                        {availableLineItems.length > 0 
                                            ? 'Start by adding the first supply to begin the usage log'
                                            : 'Generate line items first in the Production Line Items tab'
                                        }
                                    </Typography>
                                </CardContent>
                            </Card>
                        ) : (
                            <Box>
                                <Typography variant="h6" sx={{ fontWeight: 500, color: theme.palette.text.primary, mb: 3 }}>
                                    Supply Usage Timeline ({supplies.length} supplies)
                                </Typography>
                                
                                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
                                    {supplies
                                        .sort((a, b) => {
                                            // First sort by template order (cap → bottle → label → supply)
                                            const typeOrder = {
                                                'cap': 0, 'caps': 0,
                                                'bottle': 1, 'bottles': 1,
                                                'label': 2, 'labels': 2,
                                                'supply': 3, 'supplies': 3
                                            };
                                            
                                            // Get line items for type comparison
                                            const lineItemA = lineItems.find(item => item.id === a.line_item_id);
                                            const lineItemB = lineItems.find(item => item.id === b.line_item_id);
                                            
                                            const orderA = typeOrder[lineItemA?.item_type] ?? 999;
                                            const orderB = typeOrder[lineItemB?.item_type] ?? 999;
                                            
                                            // If different types, sort by type order
                                            if (orderA !== orderB) {
                                                return orderA - orderB;
                                            }
                                            
                                            // If same type, sort by usage order, then by creation time (id) for consistency
                                            const usageOrderA = a.usage_order || 0;
                                            const usageOrderB = b.usage_order || 0;
                                            
                                            if (usageOrderA !== usageOrderB) {
                                                return usageOrderA - usageOrderB;
                                            }
                                            
                                            // Final fallback: sort by ID for consistent ordering
                                            return (a.id || 0) - (b.id || 0);
                                        })
                                        .map((supply, index) => {
                                            const variance = supply.actual_quantity && supply.planned_quantity 
                                                ? ((supply.actual_quantity - supply.planned_quantity) / supply.planned_quantity * 100)
                                                : 0;
                                            const isOutOfSpec = Math.abs(variance) > 5;
                                            const isExpanded = expandedSupplies.has(supply.id);

                                            return (
                                                <Card key={supply.id} sx={{ 
                                                    borderRadius: 4,
                                                    border: `1px solid ${theme.palette.grey[400]}`,
                                                    background: 'white',
                                                    backdropFilter: 'blur(20px)',
                                                    boxShadow: `0 8px 32px ${alpha(theme.palette.primary.main, 0.12)}`,
                                                    overflow: 'hidden',
                                                    '&:hover': {
                                                        transform: 'translateY(-4px)',
                                                        boxShadow: `0 12px 48px ${alpha(theme.palette.primary.main, 0.18)}`,
                                                    },
                                                    transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)'
                                                }}>
                                                    {/* Header - Always Visible */}
                                                    <CardContent 
                                                        sx={{ 
                                                            p: 0, 
                                                            backgroundColor: 'white',
                                                            borderBottom: `1px solid ${theme.palette.grey[400]}`,
                                                            cursor: 'pointer'
                                                        }}
                                                        onClick={() => toggleSupplyExpansion(supply.id)}
                                                    >
                                                        <Box sx={{ p: 4 }}>
                                                            {/* Top Row - Name and Actions */}
                                                            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                                                                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                                                    <Box sx={{
                                                                        p: 1.5,
                                                                        borderRadius: 2,
                                                                        background: `linear-gradient(${alpha(theme.palette.primary.main, 0.1)}, ${alpha(theme.palette.primary.main, 0.05)})`,
                                                                        border: `1px solid ${alpha(theme.palette.primary.main, 0.2)}`,
                                                                        backdropFilter: 'blur(10px)',
                                                                        mr: 3,
                                                                        display: 'flex',
                                                                        flexDirection: 'column',
                                                                        alignItems: 'center',
                                                                        minWidth: '60px'
                                                                    }}>
                                                                        <Typography variant="caption" sx={{ color: theme.palette.primary.main, fontWeight: 500, fontSize: '0.7rem', lineHeight: 1 }}>
                                                                            STEP
                                                                        </Typography>
                                                        <Typography variant="h6" sx={{ fontWeight: 500, color: theme.palette.primary.main, lineHeight: 1 }}>
                                                            {index + 1}
                                                        </Typography>
                                                                    </Box>
                                                                    <Box>
                                                                        <Typography variant="h5" sx={{ fontWeight: 500, color: theme.palette.text.primary, mb: 0.5 }}>
                                                                            {supply.supply_name}
                                                                        </Typography>
                                                                        <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', alignItems: 'center' }}>
                                                                            {supply.supplier_name && (
                                                                                <Typography variant="body2" sx={{ color: theme.palette.primary.main, fontWeight: 500 }}>
                                                                                    SUPPLIER: {supply.supplier_name}
                                                                                </Typography>
                                                                            )}
                                                                        </Box>
                                                                    </Box>
                                                                </Box>
                                                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                                    <IconButton 
                                                                        size="medium" 
                                                                        onClick={(e) => {
                                                                            e.stopPropagation();
                                                                            if (canEdit) handleEditSupply(supply);
                                                                        }}
                                                                        disabled={!canEdit}
                                                                        sx={{ 
                                                                            color: theme.palette.primary.main,
                                                                            border: `1px solid ${alpha(theme.palette.grey[400], 0.3)}`,
                                                                            borderRadius: 2,
                                                                            transition: 'all 0.2s cubic-bezier(0.4, 0.0, 0.2, 1)',
                                                                            '&:hover': {
                                                                                bgcolor: theme.palette.primary.main,
                                                                                color: 'white',
                                                                                transform: 'scale(1.05)',
                                                                                boxShadow: `0 4px 12px ${alpha(theme.palette.primary.main, 0.3)}`
                                                                            }
                                                                        }}
                                                                    >
                                                                        <EditIcon />
                                                                    </IconButton>
                                                                    <IconButton 
                                                                        size="medium" 
                                                                        onClick={(e) => {
                                                                            e.stopPropagation();
                                                                            if (canDelete) confirmDelete(supply);
                                                                        }}
                                                                        disabled={!canDelete}
                                                                        sx={{ 
                                                                            color: theme.palette.error.main,
                                                                            border: `1px solid ${alpha(theme.palette.grey[400], 0.3)}`,
                                                                            borderRadius: 2,
                                                                            transition: 'all 0.2s cubic-bezier(0.4, 0.0, 0.2, 1)',
                                                                            '&:hover': {
                                                                                bgcolor: theme.palette.error.main,
                                                                                color: 'white',
                                                                                transform: 'scale(1.05)',
                                                                                boxShadow: `0 4px 12px ${alpha(theme.palette.error.main, 0.3)}`
                                                                            }
                                                                        }}
                                                                    >
                                                                        <DeleteIcon />
                                                                    </IconButton>
                                                                    <IconButton 
                                                                        size="medium"
                                                                        sx={{ 
                                                                            color: theme.palette.primary.main,
                                                                            border: `1px solid ${alpha(theme.palette.grey[400], 0.3)}`,
                                                                            borderRadius: 2,
                                                                            ml: 1,
                                                                            transition: 'all 0.2s cubic-bezier(0.4, 0.0, 0.2, 1)',
                                                                            '&:hover': {
                                                                                bgcolor: theme.palette.primary.main,
                                                                                color: 'white',
                                                                                transform: 'scale(1.05)',
                                                                                boxShadow: `0 4px 12px ${alpha(theme.palette.primary.main, 0.3)}`
                                                                            }
                                                                        }}
                                                                    >
                                                                        {isExpanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                                                                    </IconButton>
                                                                </Box>
                                                            </Box>

                                                            {/* Quantity and Status Row */}
                                                            <Box sx={{ display: 'flex', gap: 3, alignItems: 'center', flexWrap: 'wrap' }}>
                                                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                                                                    <Box sx={{ textAlign: 'center' }}>
                                                                        <Typography variant="h6" sx={{ fontWeight: 500, color: theme.palette.text.secondary, lineHeight: 1 }}>
                                                                            {supply.planned_quantity || '—'}
                                                                        </Typography>
                                                                        <Typography variant="caption" sx={{ color: theme.palette.text.disabled, fontWeight: 500 }}>
                                                                            PLANNED
                                                                        </Typography>
                                                                    </Box>
                                                                    <Typography variant="h6" sx={{ color: theme.palette.text.secondary, fontWeight: 400 }}>
                                                                        →
                                                                    </Typography>
                                                                    <Box sx={{ textAlign: 'center' }}>
                                                                        <Typography variant="h6" sx={{ 
                                                                            fontWeight: 500, 
                                                                            color: supply.actual_quantity ? theme.palette.success.main : theme.palette.text.secondary,
                                                                            lineHeight: 1
                                                                        }}>
                                                                            {supply.actual_quantity || '—'}
                                                                        </Typography>
                                                                        <Typography variant="caption" sx={{ color: theme.palette.text.disabled, fontWeight: 500 }}>
                                                                            ACTUAL
                                                                        </Typography>
                                                                    </Box>
                                                                </Box>

                                                                {supply.actual_quantity && supply.planned_quantity && (
                                                                    <Chip 
                                                                        label={`${variance > 0 ? '+' : ''}${variance.toFixed(1)}%`}
                                                                        color={isOutOfSpec ? 'error' : 'success'}
                                                                        sx={{ 
                                                                            fontWeight: 500,
                                                                            fontSize: '0.875rem',
                                                                            height: 32
                                                                        }}
                                                                    />
                                                                )}

                                                                {supply.used_by_operator && (
                                                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                                        <Typography variant="body2" sx={{ color: theme.palette.text.disabled, fontWeight: 500 }}>
                                                                            OPERATOR:
                                                                        </Typography>
                                                                        <Typography variant="body2" sx={{ color: theme.palette.text.primary, fontWeight: 500 }}>
                                                                            {supply.used_by_operator}
                                                                        </Typography>
                                                                    </Box>
                                                                )}

                                                                {supply.time_of_usage && (
                                                                    <Typography variant="body2" sx={{ 
                                                                        color: theme.palette.text.secondary,
                                                                        backgroundColor: alpha(theme.palette.grey[100], 0.8),
                                                                        px: 2,
                                                                        py: 0.5,
                                                                        borderRadius: 2,
                                                                        fontWeight: 500
                                                                    }}>
                                                                        {dayjs(supply.time_of_usage).format(lineTimeFormat === '24hour' ? 'MMM DD, YYYY HH:mm' : 'MMM DD, YYYY h:mm A')}
                                                                    </Typography>
                                                                )}
                                                            </Box>
                                                        </Box>
                                                    </CardContent>

                                                    {/* Expandable Details */}
                                                    <Collapse in={isExpanded}>
                                                        <CardContent sx={{ 
                                                            p: 4, 
                                                            backgroundColor: alpha(theme.palette.grey[50], 0.8),
                                                            borderTop: `1px solid ${alpha(theme.palette.primary.main, 0.3)}`,
                                                            transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                                                            '&:hover': {
                                                                backgroundColor: alpha(theme.palette.primary.main, 0.02),
                                                                transform: 'translateY(-1px)',
                                                                boxShadow: `0 4px 12px ${alpha(theme.palette.primary.main, 0.06)}`
                                                            }
                                                        }}>
                                                            <Grid container spacing={3}>
                                                                {/* Lot/Batch Information */}
                                                                {supply.id && (
                                                                    <Grid item xs={12}>
                                                                        <Typography variant="h6" sx={{ fontWeight: 500, mb: 3, color: theme.palette.text.primary, fontSize: '1.2rem' }}>
                                                                            Lot/Batch Information
                                                                        </Typography>
                                                                        <Grid container spacing={2}>
                                                            {/* Multiple Lots Display */}
                                                            <SupplyLotsCards 
                                                                supply={supply}
                                                                lineItemId={supply.line_item_id}
                                                                theme={theme}
                                                                alpha={alpha}
                                                            />
                                                                        </Grid>
                                                                    </Grid>
                                                                )}

                                                                {/* Operator & Timing */}
                                                                {supply.used_by_operator && (
                                                                    <Grid item xs={12}>
                                                                        <Typography variant="h6" sx={{ fontWeight: 500, mb: 3, color: theme.palette.text.primary, fontSize: '1.2rem' }}>
                                                                            Operator & Timing
                                                                        </Typography>
                                                                        <Card sx={{ 
                                                                            p: 3, 
                                                                            backgroundColor: 'white', 
                                                                            border: `1px solid ${theme.palette.grey[400]}`,
                                                                            borderRadius: 3,
                                                                            boxShadow: `0 4px 20px ${alpha(theme.palette.primary.main, 0.08)}`,
                                                                            transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                                                                            '&:hover': {
                                                                                transform: 'translateY(-2px)',
                                                                                boxShadow: `0 8px 32px ${alpha(theme.palette.primary.main, 0.12)}`
                                                                            }
                                                                        }}>
                                                                            <Typography variant="body1" sx={{ fontWeight: 500, mb: 1, color: theme.palette.text.primary }}>
                                                                                Operator: {supply.used_by_operator}
                                                                            </Typography>
                                                                            {supply.time_of_usage && (
                                                                                <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 500 }}>
                                                                                    Used: {dayjs(supply.time_of_usage).format(lineTimeFormat === '24hour' ? 'MMM DD, YYYY HH:mm' : 'MMM DD, YYYY h:mm A')}
                                                                                </Typography>
                                                                            )}
                                                                        </Card>
                                                                    </Grid>
                                                                )}

                                                                {/* Notes */}
                                                                {supply.additional_notes && (
                                                                    <Grid item xs={12}>
                                                                        <Typography variant="h6" sx={{ fontWeight: 500, mb: 3, color: theme.palette.text.primary, fontSize: '1.2rem' }}>
                                                                            Notes
                                                                        </Typography>
                                                                        <Card sx={{ 
                                                                            p: 3, 
                                                                            backgroundColor: alpha(theme.palette.warning.main, 0.05), 
                                                                            border: `1px solid ${theme.palette.warning.main}`,
                                                                            borderRadius: 3,
                                                                            boxShadow: `0 4px 20px ${alpha(theme.palette.warning.main, 0.08)}`,
                                                                            transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                                                                            '&:hover': {
                                                                                transform: 'translateY(-2px)',
                                                                                boxShadow: `0 8px 32px ${alpha(theme.palette.warning.main, 0.12)}`
                                                                            }
                                                                        }}>
                                                                            <Typography variant="body2" sx={{ fontWeight: 500, color: theme.palette.text.primary }}>
                                                                                {supply.additional_notes}
                                                                            </Typography>
                                                                        </Card>
                                                                    </Grid>
                                                                )}
                                                            </Grid>
                                                        </CardContent>
                                                    </Collapse>
                                                </Card>
                                            );
                                        })}
                                </Box>
                            </Box>
                        )}
                    </Grid>
                </Grid>
            </Box>

            {/* Delete Confirmation Dialog */}
            <Dialog 
                open={deleteConfirmOpen} 
                onClose={() => setDeleteConfirmOpen(false)}
                maxWidth="sm"
                fullWidth
                PaperProps={{
                    sx: {
                        borderRadius: 3,
                        border: `1px solid ${alpha(theme.palette.grey[400], 0.8)}`,
                        boxShadow: `0 8px 24px ${alpha(theme.palette.primary.main, 0.2)}`,
                        maxHeight: '90vh',
                        overflow: 'hidden'
                    }
                }}
            >
                <DialogTitle sx={{ 
                    fontWeight: 500,
                    fontSize: '1.2rem',
                    color: theme.palette.error.main,
                    borderBottom: `1px solid ${alpha(theme.palette.grey[400], 0.8)}`,
                    background: alpha(theme.palette.error.main, 0.05),
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    p: 3
                }}>
                    Confirm Delete Supply
                </DialogTitle>
                <DialogContent sx={{ 
                    backgroundColor: alpha(theme.palette.grey[50], 0.5), 
                    p: 3,
                    backdropFilter: 'blur(10px)',
                    mt: 3
                }}>
                    <Typography variant="body1" sx={{ mb: 2, mt: 2, fontWeight: 500 }}>
                        Are you sure you want to delete this supply from the usage log?
                    </Typography>
                    {supplyToDelete && (
                        <Box sx={{ 
                            p: 3, 
                            backgroundColor: alpha(theme.palette.grey[100], 0.8), 
                            borderRadius: 3, 
                            border: `1px solid ${theme.palette.grey[400]}`, 
                            mb: 3
                        }}>
                            <Typography variant="subtitle2" sx={{ fontWeight: 500, color: theme.palette.text.primary }}>
                                {supplyToDelete.supply_name}
                            </Typography>
                            <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 500 }}>
                                Step {supplyToDelete.usage_order}
                                {supplyToDelete.actual_quantity && ` • ${supplyToDelete.actual_quantity}`}
                                {supplyToDelete.supplier_name && ` • ${supplyToDelete.supplier_name}`}
                            </Typography>
                        </Box>
                    )}
                    <Typography variant="body2" color="error" sx={{ fontWeight: 500 }}>
                        This action cannot be undone.
                    </Typography>
                </DialogContent>
                <DialogActions sx={{ 
                    p: 3, 
                    gap: 2, 
                    borderTop: `1px solid ${alpha(theme.palette.primary.main, 0.1)}`,
                    background: `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.08)}, ${alpha(theme.palette.primary.main, 0.04)})`
                }}>
                    <Button 
                        onClick={() => setDeleteConfirmOpen(false)}
                        sx={{
                            borderRadius: 2,
                            px: 3,
                            py: 1.5,
                            color: theme.palette.text.secondary,
                            fontWeight: 500,
                            background: 'rgba(255, 255, 255, 0.8)',
                            backdropFilter: 'blur(10px)',
                            '&:hover': {
                                backgroundColor: alpha(theme.palette.primary.main, 0.04)
                            }
                        }}
                    >
                        Cancel
                    </Button>
                    <Button 
                        onClick={handleConfirmDelete}
                        variant="contained"
                        startIcon={<DeleteIcon />}
                        sx={{
                            borderRadius: 2,
                            px: 4,
                            py: 1.5,
                            background: `linear-gradient(135deg, ${theme.palette.error.main}, ${theme.palette.error.dark})`,
                            color: 'white',
                            fontWeight: 500,
                            boxShadow: `0 4px 12px ${alpha(theme.palette.error.main, 0.3)}`,
                            '&:hover': {
                                background: `linear-gradient(135deg, ${theme.palette.error.dark}, ${theme.palette.error.main})`,
                                boxShadow: `0 6px 20px ${alpha(theme.palette.error.main, 0.4)}`
                            }
                        }}
                    >
                        Delete Supply
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Add/Edit Supply Dialog */}
            <Dialog 
                open={openDialog} 
                onClose={() => setOpenDialog(false)} 
                maxWidth="lg" 
                fullWidth
                PaperProps={{
                    sx: {
                        borderRadius: 3,
                        border: `1px solid ${alpha(theme.palette.grey[400], 0.8)}`,
                        boxShadow: `0 8px 24px ${alpha(theme.palette.primary.main, 0.2)}`,
                        maxHeight: '90vh',
                        overflow: 'hidden'
                    }
                }}
            >
                <DialogTitle sx={{ 
                    fontWeight: 500,
                    fontSize: '1.2rem',
                    color: 'primary.main',
                    borderBottom: `1px solid ${alpha(theme.palette.grey[400], 0.8)}`,
                    background: alpha(theme.palette.primary.main, 0.05),
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    p: 3
                }}>
                    <Typography variant="h5" sx={{ fontWeight: 500, color: 'primary.main' }}>
                        {editingSupply ? 'Edit Supply Log' : 'Add Supply Log'}
                    </Typography>
                </DialogTitle>
                <DialogContent sx={{ 
                    backgroundColor: alpha(theme.palette.grey[50], 0.5), 
                    p: 3,
                    maxHeight: '80vh', 
                    overflowY: 'auto',
                    backdropFilter: 'blur(10px)',
                    mt: 3
                }}>
                    <Grid container spacing={3}>
                        {/* LINE ITEM SELECTION */}
                        {!editingSupply && availableLineItems.length > 0 && (
                            <Grid item xs={12} sx={{ mt: 3 }}>
                                <Card sx={{ 
                                    p: 3, 
                                    border: `1px solid ${theme.palette.grey[400]}`, 
                                    borderRadius: 3, 
                                    backgroundColor: 'white',
                                    boxShadow: `0 4px 20px ${alpha(theme.palette.primary.main, 0.08)}`,
                                    transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                                    '&:hover': {
                                        transform: 'translateY(-2px)',
                                        boxShadow: `0 8px 32px ${alpha(theme.palette.primary.main, 0.12)}`,
                                        border: `1px solid ${alpha(theme.palette.primary.main, 0.3)}`
                                    }
                                }}>
                                    <Typography variant="h6" sx={{ fontWeight: 500, mb: 2, color: theme.palette.text.primary }}>
                                        Select Line Item
                                    </Typography>
                                    <Button
                                        fullWidth
                                        variant="outlined"
                                        onClick={() => {
                                            if (canEdit) handleOpenLineItemSelection();
                                        }}
                                        disabled={!canEdit}
                                        sx={{
                                            height: 56,
                                            borderRadius: 3,
                                            border: `1px solid ${alpha(theme.palette.grey[400], 0.8)}`,
                                            background: 'white',
                                            justifyContent: 'flex-start',
                                            px: 3,
                                            py: 2.5,
                                            '&:hover': {
                                                border: `1px solid ${theme.palette.primary.main}`,
                                                boxShadow: `0 2px 8px ${alpha(theme.palette.primary.main, 0.15)}`
                                            }
                                        }}
                                    >
                                        <Box sx={{ display: 'flex', alignItems: 'center', width: '100%' }}>
                                            <AllInboxIcon sx={{ mr: 2, color: 'text.secondary' }} />
                                            <Box sx={{ textAlign: 'left', flex: 1 }}>
                                                {formData.line_item_id ? (
                                                    <>
                                                        <Typography variant="body1" sx={{ fontWeight: 500, fontSize: '0.9rem' }}>
                                                            {formData.supply_name}
                                                        </Typography>
                                                        {formData.supplier_name && (
                                                            <Typography variant="caption" color="text.secondary">
                                                                {formData.supplier_name}
                                                            </Typography>
                                                        )}
                                                    </>
                                                ) : (
                                                    <Typography variant="body1" sx={{ fontWeight: 500, fontSize: '0.9rem', color: 'text.secondary' }}>
                                                        Choose from existing line items
                                                    </Typography>
                                                )}
                                            </Box>
                                        </Box>
                                    </Button>
                                </Card>
                            </Grid>
                        )}

                        {/* SUPPLY DETAILS */}
                        <Grid item xs={12}>
                            <Card sx={{ 
                                p: 3, 
                                border: `1px solid ${theme.palette.grey[400]}`, 
                                borderRadius: 3, 
                                backgroundColor: 'white',
                                boxShadow: `0 4px 20px ${alpha(theme.palette.primary.main, 0.08)}`,
                                transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                                '&:hover': {
                                    boxShadow: `0 8px 32px ${alpha(theme.palette.primary.main, 0.12)}`
                                }
                            }}>
                                <Typography variant="h6" sx={{ fontWeight: 500, mb: 2, color: theme.palette.text.primary }}>
                                    Supply Details
                                </Typography>
                                <Grid container spacing={2}>
                                    <Grid item xs={12} md={6}>
                                        <TextField
                                            fullWidth
                                            label="Supply Name"
                                            value={formData.supply_name}
                                            onChange={(e) => canEdit ? setFormData({...formData, supply_name: e.target.value}) : null}
                                            disabled={!formData.line_item_id || !canEdit}
                                            required
                                        />
                                    </Grid>
                                    <Grid item xs={12} md={6}>
                                        <TextField
                                            fullWidth
                                            label="Supplier"
                                            value={formData.supplier_name}
                                            onChange={(e) => canEdit ? setFormData({...formData, supplier_name: e.target.value}) : null}
                                            disabled={!formData.line_item_id || !canEdit}
                                            placeholder={canEdit ? "Supplier name" : "Supplier name (read-only)"}
                                        />
                                    </Grid>
                                </Grid>

                                {/* Multiple Lots Section */}
                                {supplyLots.length > 0 && (
                                    <>
                                        <Typography variant="h6" sx={{ fontWeight: 500, mt: 3, mb: 2, color: theme.palette.text.primary }}>
                                            Lot/Batch Information
                                        </Typography>
                                        <Grid container spacing={2}>
                                            {supplyLots.map((lot, index) => (
                                                <Grid item xs={12} sm={6} md={4} key={lot.id}>
                                                    <Card sx={{
                                                        p: 2,
                                                        border: `1px solid ${theme.palette.grey[300]}`,
                                                        borderRadius: 2,
                                                        backgroundColor: alpha(theme.palette.info.main, 0.05),
                                                        transition: 'all 0.2s ease',
                                                        '&:hover': {
                                                            transform: 'translateY(-1px)',
                                                            boxShadow: `0 4px 12px ${alpha(theme.palette.info.main, 0.15)}`
                                                        }
                                                    }}>
                                                        <Typography variant="subtitle2" sx={{ 
                                                            fontWeight: 500, 
                                                            color: theme.palette.info.main,
                                                            mb: 1
                                                        }}>
                                                            {lot.lot_number ? `Lot ${lot.lot_number}` : `Batch ${lot.batch_number}`}
                                                        </Typography>
                                                        <Typography variant="body2" sx={{ 
                                                            color: theme.palette.text.secondary,
                                                            mb: 0.5
                                                        }}>
                                                            Quantity: {lot.quantity_used || lot.quantity_on_hand} {lot.unit_used || lot.unit_of_measure}
                                                        </Typography>
                                                        {lot.location?.name && (
                                                            <Typography variant="caption" sx={{ 
                                                                color: theme.palette.text.secondary,
                                                                display: 'block'
                                                            }}>
                                                                Location: {lot.location.name}
                                                            </Typography>
                                                        )}
                                                        {lot.expiry_date && (
                                                            <Typography variant="caption" sx={{ 
                                                                color: theme.palette.text.secondary,
                                                                display: 'block'
                                                            }}>
                                                                Expires: {new Date(lot.expiry_date).toLocaleDateString()}
                                                            </Typography>
                                                        )}
                                                    </Card>
                                                </Grid>
                                            ))}
                                        </Grid>
                                    </>
                                )}
                            </Card>
                        </Grid>

                        {/* QUANTITIES */}
                        <Grid item xs={12}>
                            <Card sx={{ 
                                p: 3, 
                                border: `1px solid ${theme.palette.grey[400]}`, 
                                borderRadius: 3, 
                                backgroundColor: 'white',
                                boxShadow: `0 4px 20px ${alpha(theme.palette.primary.main, 0.08)}`,
                                transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                                '&:hover': {
                                    transform: 'translateY(-2px)',
                                    boxShadow: `0 8px 32px ${alpha(theme.palette.primary.main, 0.12)}`,
                                    border: `1px solid ${alpha(theme.palette.primary.main, 0.3)}`
                                }
                            }}>
                                <Typography variant="h6" sx={{ fontWeight: 500, mb: 2, color: theme.palette.text.primary }}>
                                    Quantities
                                </Typography>
                                <Grid container spacing={2}>
                                    <Grid item xs={12} md={6}>
                                        <TextField
                                            fullWidth
                                            label="Planned Quantity"
                                            type="number"
                                            value={formData.planned_quantity}
                                            onChange={(e) => canEdit ? setFormData({...formData, planned_quantity: e.target.value}) : null}
                                            inputProps={{ step: 0.001 }}
                                            disabled={!formData.line_item_id || !canEdit}
                                        />
                                    </Grid>
                                    <Grid item xs={12} md={6}>
                                        <TextField
                                            fullWidth
                                            label="Actual Quantity"
                                            type="number"
                                            value={formData.actual_quantity}
                                            onChange={(e) => canEdit ? setFormData({...formData, actual_quantity: e.target.value}) : null}
                                            inputProps={{ step: 0.001 }}
                                            required
                                            disabled={!formData.line_item_id || !canEdit}
                                        />
                                    </Grid>
                                </Grid>
                            </Card>
                        </Grid>

                        {/* TIMING & OPERATOR */}
                        <Grid item xs={12}>
                            <Card sx={{ 
                                p: 3, 
                                border: `1px solid ${theme.palette.grey[400]}`, 
                                borderRadius: 3, 
                                backgroundColor: 'white',
                                boxShadow: `0 4px 20px ${alpha(theme.palette.primary.main, 0.08)}`,
                                transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                                '&:hover': {
                                    transform: 'translateY(-2px)',
                                    boxShadow: `0 8px 32px ${alpha(theme.palette.primary.main, 0.12)}`,
                                    border: `1px solid ${alpha(theme.palette.primary.main, 0.3)}`
                                }
                            }}>
                                <Typography variant="h6" sx={{ fontWeight: 500, mb: 2, color: theme.palette.text.primary }}>
                                    Timing & Operator
                                </Typography>
                                <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '8px' }}>
                                    <DateTimePicker
                                        label="Time of Usage"
                                        value={formData.time_of_usage}
                                        onChange={(newValue) => canEdit ? setFormData({...formData, time_of_usage: newValue}) : null}
                                        format={lineTimeFormat === '24hour' ? 'MM/DD/YYYY HH:mm' : 'MM/DD/YYYY h:mm A'}
                                        ampm={lineTimeFormat !== '24hour'}
                                        slotProps={{
                                            textField: {
                                                fullWidth: true,
                                                disabled: !formData.line_item_id || !canEdit
                                            }
                                        }}
                                        disabled={!formData.line_item_id || !canEdit}
                                    />
                                    <TextField
                                        fullWidth
                                        label="Used by Operator"
                                        value={formData.used_by_operator}
                                        onChange={(e) => canEdit ? setFormData({...formData, used_by_operator: e.target.value}) : null}
                                        required
                                        disabled={!formData.line_item_id || !canEdit}
                                    />
                                </Box>
                            </Card>
                        </Grid>

                        {/* NOTES */}
                        <Grid item xs={12}>
                            <Card sx={{ 
                                p: 3, 
                                border: `1px solid ${theme.palette.grey[400]}`, 
                                borderRadius: 3, 
                                backgroundColor: 'white',
                                boxShadow: `0 4px 20px ${alpha(theme.palette.primary.main, 0.08)}`,
                                transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                                '&:hover': {
                                    transform: 'translateY(-2px)',
                                    boxShadow: `0 8px 32px ${alpha(theme.palette.primary.main, 0.12)}`,
                                    border: `1px solid ${alpha(theme.palette.primary.main, 0.3)}`
                                }
                            }}>
                                <Typography variant="h6" sx={{ fontWeight: 500, mb: 2, color: theme.palette.text.primary }}>
                                    Additional Notes
                                </Typography>
                                <TextField
                                    fullWidth
                                    multiline
                                    rows={4}
                                    label="Usage Notes"
                                    value={formData.additional_notes}
                                    onChange={(e) => canEdit ? setFormData({...formData, additional_notes: e.target.value}) : null}
                                    placeholder={canEdit ? "Enter any notes about supply usage, observations, or special handling..." : "Usage notes (read-only)"}
                                    disabled={!formData.line_item_id || !canEdit}
                                />
                            </Card>
                        </Grid>
                    </Grid>
                </DialogContent>
                <DialogActions sx={{ 
                    p: 3, 
                    gap: 2, 
                    borderTop: `1px solid ${alpha(theme.palette.primary.main, 0.1)}`,
                    background: `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.08)}, ${alpha(theme.palette.primary.main, 0.04)})`
                }}>
                    <Button onClick={() => {
                        setOpenDialog(false);
                    }} 
                    startIcon={<CancelIcon />}
                    sx={{ 
                        borderRadius: 2,
                        px: 3,
                        py: 1.5,
                        color: theme.palette.text.secondary,
                        fontWeight: 500,
                        background: 'rgba(255, 255, 255, 0.8)',
                        backdropFilter: 'blur(10px)',
                        '&:hover': {
                            backgroundColor: alpha(theme.palette.primary.main, 0.04)
                        }
                    }}>
                        Cancel
                    </Button>
                    <Button 
                        onClick={handleSaveSupply} 
                        variant="contained"
                        startIcon={<SaveIcon />}
                        disabled={
                            !formData.line_item_id ||
                            !formData.supply_name ||
                            !formData.actual_quantity ||
                            !formData.time_of_usage ||
                            !formData.used_by_operator ||
                            !canEdit
                        }
                        sx={{
                            borderRadius: 2,
                            px: 4,
                            py: 1.5,
                            background: `linear-gradient(135deg, ${theme.palette.primary.main}, ${theme.palette.primary.dark})`,
                            color: 'white',
                            fontWeight: 500,
                            boxShadow: `0 4px 12px ${alpha(theme.palette.primary.main, 0.3)}`,
                            '&:hover': {
                                background: `linear-gradient(135deg, ${theme.palette.primary.dark}, ${theme.palette.primary.main})`,
                                boxShadow: `0 6px 20px ${alpha(theme.palette.primary.main, 0.4)}`
                            },
                            '&:disabled': {
                                background: theme.palette.grey[400],
                                color: theme.palette.grey[100]
                            }
                        }}
                    >
                        Save Supply
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Line Item Selection Dialog */}
            <Dialog
                open={lineItemSelectionDialogOpen}
                onClose={handleCloseLineItemSelection}
                maxWidth="lg"
                fullWidth
                PaperProps={{
                    sx: {
                        borderRadius: 3,
                        border: `1px solid ${alpha(theme.palette.grey[400], 0.8)}`,
                        boxShadow: `0 8px 24px ${alpha(theme.palette.primary.main, 0.2)}`,
                        maxHeight: '90vh'
                    }
                }}
            >
                <DialogTitle sx={{ 
                    fontWeight: 500,
                    fontSize: '1.2rem',
                    color: 'primary.main',
                    borderBottom: `1px solid ${alpha(theme.palette.grey[400], 0.8)}`,
                    background: alpha(theme.palette.primary.main, 0.05),
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between'
                }}>
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                        <AllInboxIcon sx={{ mr: 2 }} />
                        Select Line Item
                    </Box>
                    <IconButton 
                        onClick={handleCloseLineItemSelection} 
                        size="small"
                        sx={{
                            borderRadius: 2,
                            border: `1px solid ${alpha(theme.palette.grey[400], 0.8)}`,
                            '&:hover': {
                                transform: 'scale(1.1)',
                                border: `1px solid ${theme.palette.primary.main}`,
                                boxShadow: `0 2px 8px ${alpha(theme.palette.grey[900], 0.2)}`
                            }
                        }}
                    >
                        <CloseIcon />
                    </IconButton>
                </DialogTitle>
                <DialogContent sx={{ p: 0 }}>
                    <Box sx={{ p: 3, borderBottom: `1px solid ${alpha(theme.palette.grey[400], 0.8)}` }}>
                        <TextField
                            fullWidth
                            placeholder="Search items by name, supplier, lot number, or notes..."
                            value={lineItemSearchTerm}
                            onChange={(e) => setLineItemSearchTerm(e.target.value)}
                            InputProps={{
                                startAdornment: (
                                    <InputAdornment position="start">
                                        <SearchIcon color="action" />
                                    </InputAdornment>
                                ),
                                sx: {
                                    borderRadius: 3,
                                    border: `1px solid ${alpha(theme.palette.grey[400], 0.8)}`,
                                    background: 'white',
                                    fontSize: '0.9rem',
                                    '& fieldset': { border: 'none' },
                                    '&:hover': {
                                        border: `1px solid ${theme.palette.primary.main}`,
                                        boxShadow: `0 2px 8px ${alpha(theme.palette.primary.main, 0.15)}`
                                    },
                                    '&.Mui-focused': {
                                        border: `1px solid ${theme.palette.primary.main}`,
                                        boxShadow: `0 2px 8px ${alpha(theme.palette.primary.main, 0.25)}`
                                    }
                                }
                            }}
                        />
                    </Box>
                    <TableContainer sx={{ maxHeight: 400 }}>
                        <Table stickyHeader>
                            <TableHead>
                                <TableRow>
                                    <TableCell sx={{ 
                                        fontWeight: 500, 
                                        fontSize: '0.9rem',
                                        background: alpha(theme.palette.primary.main, 0.05),
                                        borderBottom: `1px solid ${alpha(theme.palette.grey[400], 0.8)}`
                                    }}>
                                        Item
                                    </TableCell>
                                    <TableCell sx={{ 
                                        fontWeight: 500, 
                                        fontSize: '0.9rem',
                                        background: alpha(theme.palette.primary.main, 0.05),
                                        borderBottom: `1px solid ${alpha(theme.palette.grey[400], 0.8)}`
                                    }}>
                                        Supplier
                                    </TableCell>
                                    <TableCell sx={{ 
                                        fontWeight: 500, 
                                        fontSize: '0.9rem',
                                        background: alpha(theme.palette.primary.main, 0.05),
                                        borderBottom: `1px solid ${alpha(theme.palette.grey[400], 0.8)}`
                                    }}>
                                        Quantity
                                    </TableCell>
                                    <TableCell sx={{ 
                                        fontWeight: 500, 
                                        fontSize: '0.9rem',
                                        background: alpha(theme.palette.primary.main, 0.05),
                                        borderBottom: `1px solid ${alpha(theme.palette.grey[400], 0.8)}`,
                                        width: 100
                                    }}>
                                        Action
                                    </TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {getFilteredLineItems().length === 0 ? (
                                    <TableRow>
                                        <TableCell colSpan={4} sx={{ textAlign: 'center', py: 4 }}>
                                            <Typography color="text.secondary">
                                                {lineItemSearchTerm ? 'No line items found matching your search.' : 'No line items available.'}
                                            </Typography>
                                        </TableCell>
                                    </TableRow>
                                ) : (
                                    getFilteredLineItems().map((lineItem) => (
                                        <TableRow
                                            key={lineItem.id}
                                            hover
                                            sx={{
                                                cursor: 'pointer',
                                                '&:hover': {
                                                    backgroundColor: alpha(theme.palette.primary.main, 0.05)
                                                }
                                            }}
                                            onClick={() => handleSelectLineItem(lineItem)}
                                        >
                                            <TableCell>
                                                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                                    {getItemIcon(lineItem.item_type)}
                                                    <Box sx={{ ml: 2 }}>
                                                        <Typography variant="body2" sx={{ fontWeight: 500, fontSize: '0.9rem' }}>
                                                            {getItemName(lineItem) || 'Unknown'}
                                                        </Typography>
                                                        <Typography variant="caption" color="text.secondary">
                                                            {lineItem.item_type}
                                                        </Typography>
                                                    </Box>
                                                </Box>
                                            </TableCell>
                                            <TableCell>
                                                <Typography variant="body2" sx={{ fontSize: '0.8rem' }}>
                                                    {getSupplierName(lineItem) || '—'}
                                                </Typography>
                                            </TableCell>
                                            <TableCell>
                                                <Typography variant="body2" sx={{ fontSize: '0.8rem' }}>
                                                    {lineItem.planned_quantity || '—'}
                                                    {lineItem.actual_quantity && lineItem.actual_quantity !== lineItem.planned_quantity && (
                                                        <>
                                                            <br />
                                                            <span style={{ color: theme.palette.success.main }}>
                                                                Actual: {lineItem.actual_quantity}
                                                            </span>
                                                        </>
                                                    )}
                                                </Typography>
                                            </TableCell>
                                            <TableCell>
                                                <Button
                                                    size="small"
                                                    variant="contained"
                                                    onClick={(e) => {
                                                        e.stopPropagation();
                                                        handleSelectLineItem(lineItem);
                                                    }}
                                                    sx={{
                                                        borderRadius: 2,
                                                        fontWeight: 500,
                                                        fontSize: '0.8rem',
                                                        px: 2,
                                                        py: 0.5,
                                                        '&:hover': {
                                                            transform: 'translateY(-1px)',
                                                            boxShadow: `0 2px 8px ${alpha(theme.palette.primary.main, 0.25)}`
                                                        }
                                                    }}
                                                >
                                                    Select
                                                </Button>
                                            </TableCell>
                                        </TableRow>
                                    ))
                                )}
                            </TableBody>
                        </Table>
                    </TableContainer>
                </DialogContent>
                <DialogActions sx={{ p: 3, borderTop: `1px solid ${alpha(theme.palette.grey[400], 0.8)}` }}>
                    <Button 
                        onClick={handleCloseLineItemSelection}
                        sx={{
                            borderRadius: 2,
                            border: `1px solid ${alpha(theme.palette.grey[400], 0.8)}`,
                            fontWeight: 500,
                            px: 3,
                            py: 1,
                            '&:hover': {
                                transform: 'translateY(-1px)',
                                border: `1px solid ${theme.palette.primary.main}`,
                                boxShadow: `0 2px 8px ${alpha(theme.palette.grey[900], 0.15)}`
                            }
                        }}
                    >
                        Cancel
                    </Button>
                </DialogActions>
            </Dialog>
        </LocalizationProvider>
    );
}